import React, { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SidePanel } from '@ui/layout';
import { Activity } from '@shared/models/design/activities/activity.model';
import {
  ActivityType,
  activityTypes,
} from '@shared/models/design/activities/activity-type.model';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { useBuildingDesign } from '../hooks/design.hooks';
import { useAddActivity, useRemoveActivity } from '../hooks/activities.hooks';
import { useDesignPanelScope } from '../hooks/use-design-panel-scope.hook';
import { ActivityRow } from './ActivityRow';
import { AddActivityButton } from './AddActivityButton';
import { sumProperties } from '@shared/helpers/math.helpers';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { ActivityGroupHeaderRow } from './ActivityGroupHeaderRow';
import { createActivityOfType } from '@shared/helpers/design/factories';
import { throwIfError } from '@shared/helpers/utils.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import amplitudeLog from '@client/amplitude';
import {
  AMP_DESIGN_ACTIVITY_ADD,
  AMP_DESIGN_ACTIVITY_DELETE,
} from '@shared/constants';
import {
  ignoreDesignSnapshotSuperseded,
  isDesignSnapshotSupersededError,
} from '../helpers/design-snapshot-persist.helpers';

const useStyles = makeStyles()((theme) => ({
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  emptyState: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

/**
 * Activities panel showing the building design area split per activity type.
 */
export const Activities: React.FC = () => {
  const { classes } = useStyles();
  const { activities } = useBuildingDesign();
  const addActivity = useAddActivity();
  const removeActivity = useRemoveActivity();
  const { activityMix, otherActivityArea } = useDesignPanelScope();
  const [isOtherActivitiesExpanded, setIsOtherActivitiesExpanded] =
    useState(false);
  const isReadonly = useIsReadonly();

  const totalArea = sumProperties(activityMix);

  const availableTypes = useMemo(() => {
    const usedTypes = new Set<ActivityType>(activities.map(({ type }) => type));
    return activityTypes.filter(
      (type) =>
        !usedTypes.has(type) &&
        !activityRefGuards.is.calculatedAreaActivityType(type), // Let us add calculated area activities later
    );
  }, [activities]);

  const handleSelectType = useCallback(
    async (type: ActivityType) => {
      if (isReadonly) return;
      try {
        await addActivity({
          type,
          name: getActivityTypeLabel(type),
        });
      } catch (error: unknown) {
        if (isDesignSnapshotSupersededError(error)) return;
        throw error;
      }
      amplitudeLog(AMP_DESIGN_ACTIVITY_ADD, {
        ActivityType: getActivityTypeLabel(type),
      });
    },
    [addActivity, isReadonly],
  );

  const handleRemoveActivity = useCallback(
    (activity: Activity) => {
      if (isReadonly) return;
      void ignoreDesignSnapshotSuperseded(removeActivity(activity));
      amplitudeLog(AMP_DESIGN_ACTIVITY_DELETE, {
        ActivityType: getActivityTypeLabel(activity.type),
      });
    },
    [isReadonly, removeActivity],
  );

  const programActivities = useMemo(() => {
    return activities.filter((activity) =>
      activityRefGuards.is.programActivityType(activity.type),
    );
  }, [activities]);

  const otherActivities = useMemo(() => {
    return activities.filter((activity) =>
      activityRefGuards.is.otherActivityType(activity.type),
    );
  }, [activities]);
  const toggleOtherActivitiesExpanded = useCallback(() => {
    setIsOtherActivitiesExpanded((expanded) => !expanded);
  }, []);

  const unsetArea = activityMix[ActivityType.Unset] ?? 0;
  const unsetActivity = useMemo(
    () => throwIfError(createActivityOfType(ActivityType.Unset)),
    [],
  );

  return (
    <SidePanel.Content.Section>
      <SidePanel.Content.Section.Header
        rightContent={
          <AddActivityButton
            availableTypes={availableTypes}
            onSelect={handleSelectType}
            disabled={isReadonly}
          />
        }
      />
      <div className={classes.sectionContent}>
        {programActivities.length === 0 ? (
          <Typography variant="body2" className={classes.emptyState}>
            Add a program to start allocating area.
          </Typography>
        ) : (
          <ul className={classes.list}>
            {programActivities.map((activity) => (
              <ActivityRow
                key={`program-${activity.id}`}
                activity={activity}
                area={activityMix[activity.type] ?? 0}
                totalArea={totalArea}
                onRemove={isReadonly ? undefined : handleRemoveActivity}
              />
            ))}

            {/* Don't show unset activity row if it's less than 1m², to avoid rounding errors */}
            {unsetArea > 1 && (
              <ActivityRow
                key="program-unset"
                activity={unsetActivity}
                area={unsetArea}
                totalArea={totalArea}
              />
            )}
            {otherActivities.length > 0 && (
              <ActivityGroupHeaderRow
                label="Other"
                isExpanded={isOtherActivitiesExpanded}
                onToggleExpanded={toggleOtherActivitiesExpanded}
                area={otherActivityArea}
                totalArea={totalArea}
              />
            )}
            {isOtherActivitiesExpanded &&
              otherActivities.map((activity) => (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  area={activityMix[activity.type] ?? 0}
                  totalArea={totalArea}
                  onRemove={isReadonly ? undefined : handleRemoveActivity}
                  showColorDot={false}
                />
              ))}
          </ul>
        )}
      </div>
    </SidePanel.Content.Section>
  );
};
